import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Loader2 } from 'lucide-react';
import { getDeckMachineSurface } from '../../services/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

/*
 * Machine · Live. What the machine is spending this minute, read from the
 * builder's per-call router receipts (GET /api/super/machine/live). Polls
 * every few seconds and stops while the browser tab is hidden.
 */

type Kind = 'planner' | 'workspace' | 'builder' | 'qa' | 'delivery';

interface SeriesRow { minute: number; kind: Kind; run: string; project_id: string | null; model: string | null; cost_usd: number; calls: number; errors: number }
interface WorkingRow {
  run: string; kind: Kind; client_id: string | null; project_id: string | null; project_title: string | null;
  working_on: string | null; model: string | null; started_at: string | null; started_before_window: boolean;
  last_call_at: string; spent_usd: number; calls: number;
}
interface LiveData {
  available?: boolean;
  reason?: string;
  generated_at: string;
  window_start: string;
  window_minutes: number;
  kpis: {
    spending_now_per_hour: number; models_per_hour: number; boxes_per_hour: number;
    model_rate_minutes: number; models_window_usd: number; models_today_usd: number;
    agents_working: number; clients_working: number;
    boxes_running: number; boxes_marked_running: number; box_source: 'e2b' | 'billing';
    calls_per_minute: number; errors_10m: number; untrusted_calls_window: number;
  };
  series: SeriesRow[];
  working: WorkingRow[];
  runs: Record<string, { kind: Kind; client_id: string | null; project_id: string | null; label: string | null }>;
  today_by_client: Array<{ client_id: string; models_usd: number }>;
  clients: Record<string, string>;
  projects: Record<string, { title: string; client_id: string | null }>;
}

// const POLL_MS = 5000;
const KINDS: Kind[] = ['planner', 'workspace', 'builder', 'qa', 'delivery'];
const KIND_LABEL: Record<Kind, string> = {
  planner: 'Planner', workspace: 'Open Workspace', builder: 'Builder', qa: 'QA', delivery: 'Delivery',
};
// Validated categorical slots 1-5 (dataviz validate_palette.js): light on
// #ffffff passes with a contrast WARN relieved by the labelled legend and the
// working table; dark on #1e2230 passes every check.
const KIND_COLOR: Record<string, Record<Kind, string>> = {
  light: { planner: '#2a78d6', workspace: '#eb6834', builder: '#1baf7a', qa: '#eda100', delivery: '#e87ba4' },
  dark: { planner: '#3987e5', workspace: '#d95926', builder: '#199e70', qa: '#c98500', delivery: '#d55181' },
};
const GRID: Record<string, { grid: string; tick: string }> = {
  light: { grid: '#EFEFEC', tick: '#92949D' },
  dark: { grid: '#2b3142', tick: '#6b7690' },
};
const LEGEND_MINUTES = 5;

const usd = (n: number, digits = 2): string => {
  const v = Number(n) || 0;
  if (v > 0 && v < 0.01) return '<$0.01';
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

const plural = (n: number, word: string, many?: string): string => {
  if (n === 1) return `1 ${word}`;
  if (many) return `${n.toLocaleString()} ${many}`;
  return `${n.toLocaleString()} ${word}s`;
};

const secondsSince = (iso: string | null, nowMs: number): number => {
  if (!iso) return 0;
  return Math.max(0, Math.round((nowMs - new Date(iso).getTime()) / 1000));
};

const duration = (seconds: number): string => {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} m ${seconds % 60} s`;
  return `${Math.floor(m / 60)} h ${m % 60} m`;
};

const shortModel = (model: string | null): string => {
  if (!model) return 'Unknown';
  const parts = model.split('/');
  return parts[parts.length - 1];
};

const readTheme = (): string => {
  try {
    return localStorage.getItem('aa_theme') || 'light';
  } catch {
    return 'light';
  }
};

const Kpi: React.FC<{ label: string; span: string; value: string; foot: React.ReactNode }> = ({ label, span, value, foot }) => (
  <div className="min-w-0 bg-white px-5 py-4">
    <div className="flex items-start justify-between gap-2">
      <span className="min-w-0 truncate text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">{label}</span>
      <span className="shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-gray-600">{span}</span>
    </div>
    <div className="mt-3 text-[26px] font-semibold leading-none tracking-[-0.02em] text-gray-900 tabular-nums">{value}</div>
    <div className="mt-2.5 text-[12px] leading-[17px] text-gray-500">{foot}</div>
  </div>
);

const FilterSelect: React.FC<{ label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }> = ({ label, value, onChange, options }) => (
  <label className="flex min-w-0 flex-col gap-1">
    <span className="text-[11px] font-semibold text-gray-500">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-[13px] text-gray-900 focus:border-gray-900 focus:outline-none"
    >
      <option value="">All</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

const LiveTab: React.FC = () => {
  const [data, setData] = useState<LiveData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number>(0);
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const [theme, setTheme] = useState<string>(readTheme);
  const [client, setClient] = useState('');
  const [project, setProject] = useState('');
  const [kind, setKind] = useState('');
  const [model, setModel] = useState('');
  const [run, setRun] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await getDeckMachineSurface('live');
      setData(d);
      setError(null);
      setFetchedAt(Date.now());
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load live spend');
    }
  }, []);

  useEffect(() => {
    load();
    // Polling is off for now: every 5 s it reloaded the builder's live query.
    // const poll = setInterval(() => {
    //   if (document.visibilityState === 'visible') load();
    // }, POLL_MS);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    // const onVisible = () => {
    //   if (document.visibilityState === 'visible') load();
    // };
    // document.addEventListener('visibilitychange', onVisible);
    return () => {
      // clearInterval(poll);
      clearInterval(tick);
      // document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => {
    const sync = () => setTheme(readTheme());
    window.addEventListener('aa-theme', sync);
    return () => window.removeEventListener('aa-theme', sync);
  }, []);

  const runClient = useCallback((runKey: string): string | null => data?.runs?.[runKey]?.client_id || null, [data]);

  const seriesShown = useMemo(() => {
    if (!data?.series) return [];
    return data.series.filter((s) => {
      if (client && runClient(s.run) !== client) return false;
      if (project && s.project_id !== project) return false;
      if (kind && s.kind !== kind) return false;
      if (model && s.model !== model) return false;
      if (run && s.run !== run) return false;
      return true;
    });
  }, [data, client, project, kind, model, run, runClient]);

  const workingShown = useMemo(() => {
    if (!data?.working) return [];
    return data.working.filter((w) => {
      if (client && w.client_id !== client) return false;
      if (project && w.project_id !== project) return false;
      if (kind && w.kind !== kind) return false;
      if (model && w.model !== model) return false;
      if (run && w.run !== run) return false;
      return true;
    });
  }, [data, client, project, kind, model, run]);

  const minutes = data?.window_minutes || 60;

  const perKind = useMemo(() => {
    const out: Record<Kind, number[]> = { planner: [], workspace: [], builder: [], qa: [], delivery: [] };
    for (const k of KINDS) out[k] = new Array(minutes).fill(0);
    for (const s of seriesShown) {
      if (s.minute >= 0 && s.minute < minutes) out[s.kind][s.minute] += s.cost_usd;
    }
    return out;
  }, [seriesShown, minutes]);

  const chartTotal = useMemo(() => {
    let cost = 0;
    let calls = 0;
    for (const s of seriesShown) {
      cost += s.cost_usd;
      calls += s.calls;
    }
    return { cost, calls };
  }, [seriesShown]);

  const legendRate = (k: Kind): number => {
    const tail = perKind[k].slice(-LEGEND_MINUTES);
    return tail.reduce((a, b) => a + b, 0) / LEGEND_MINUTES;
  };

  const options = useMemo(() => {
    if (!data) return { clients: [], projects: [], models: [], runs: [] };
    const clients = Object.entries(data.clients || {}).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    const projects = Object.entries(data.projects || {})
      .filter(([, p]) => !client || p.client_id === client)
      .map(([value, p]) => ({ value, label: p.title })).sort((a, b) => a.label.localeCompare(b.label));
    const modelSet = new Set<string>();
    for (const s of data.series || []) if (s.model) modelSet.add(s.model);
    const models = Array.from(modelSet).sort().map((m) => ({ value: m, label: shortModel(m) }));
    const runs = Object.entries(data.runs || {})
      .filter(([, r]) => (!client || r.client_id === client) && (!project || r.project_id === project) && (!kind || r.kind === kind))
      .map(([value, r]) => ({ value, label: `${KIND_LABEL[r.kind]}: ${r.label || value}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { clients, projects, models, runs };
  }, [data, client, project, kind]);

  const colors = KIND_COLOR[theme] || KIND_COLOR.light;
  const axis = GRID[theme] || GRID.light;

  const chartData = useMemo(() => {
    let start = Date.now();
    if (data) start = new Date(data.window_start).getTime();
    const labels = new Array(minutes).fill(0).map((_, i) => new Date(start + i * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    return {
      labels,
      datasets: KINDS.map((k) => ({
        label: KIND_LABEL[k],
        data: perKind[k].map((v) => Math.round(v * 10000) / 10000),
        borderColor: colors[k],
        backgroundColor: colors[k],
        borderWidth: 2,
        cubicInterpolationMode: 'monotone' as const,
        pointRadius: 0,
        pointHoverRadius: 4,
      })),
    };
  }, [data, perKind, minutes, colors]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (c: any) => `${c.dataset.label}: ${usd(Number(c.parsed.y), 2)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: axis.grid },
        ticks: {
          color: axis.tick,
          font: { size: 11 },
          maxRotation: 0,
          autoSkip: false,
          callback: (_v: any, i: number) => {
            if (i === 0) return `${minutes} min ago`;
            if (i === Math.floor(minutes / 2)) return `${minutes - Math.floor(minutes / 2)} min ago`;
            if (i === minutes - 1) return 'now';
            return '';
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: axis.grid },
        border: { display: false },
        ticks: { color: axis.tick, font: { size: 11 }, maxTicksLimit: 4, callback: (v: any) => usd(Number(v), 2) },
      },
    },
  }), [axis, minutes]);

  if (!data && !error) {
    return <div className="grid place-items-center py-32 text-gray-400"><Loader2 className="animate-spin" /></div>;
  }

  const unavailable = data?.available === false;
  const k = data?.kpis;
  const age = Math.round((nowMs - fetchedAt) / 1000);
  let pill = { text: `Live · ${age} s ago`, cls: 'bg-green-50 text-green-700', dot: 'bg-green-600' };
  if (age > 20 || error) pill = { text: `Stale · ${age} s ago`, cls: 'bg-amber-50 text-amber-700', dot: 'bg-amber-600' };
  if (unavailable) pill = { text: 'Builder unreachable', cls: 'bg-red-50 text-red-700', dot: 'bg-red-600' };

  const clientName = (id: string | null): string => {
    if (!id || id === 'platform') return 'Platform';
    return data?.clients?.[id] || 'Unknown client';
  };

  const anyFilter = Boolean(client || project || kind || model || run);
  const clearFilters = () => {
    setClient(''); setProject(''); setKind(''); setModel(''); setRun('');
  };

  let boxesFoot: React.ReactNode = '';
  if (k) {
    // let checkedWith = 'checked with E2B';
    // if (k.box_source === 'billing') checkedWith = 'E2B not reachable, from the last billing check';
    const notRunning = k.boxes_marked_running - k.boxes_running;
    boxesFoot = (
      <>
        {/* <span className="block">{usd(k.boxes_per_hour)} per hour while running · {checkedWith}</span> */}
        {notRunning > 0 && (
          <span className="block text-gray-400">{plural(notRunning, 'other box', 'other boxes')} show running in our records, but E2B has them paused or stopped.</span>
        )}
      </>
    );
  }
  let modelFoot = 'Every model call on the chart below, added up';
  if (anyFilter) modelFoot = `${modelFoot}, for the filters chosen`;
  let workingTitle = 'Working right now';
  if (k) workingTitle = `Working right now · ${plural(k.agents_working, 'agent')}`;

  return (
    <div className="mx-auto w-full max-w-[1200px]">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">Machine · Watch</p>
          <h1 className="m-0 text-[28px] font-bold leading-tight tracking-[-0.015em] text-gray-900">Live</h1>
          <p className="m-0 mt-1.5 max-w-[640px] text-[14px] text-gray-500">Every agent working right now, what it costs this minute, and where the money goes. Reload the page to refresh.</p>
        </div>
        <span className={`inline-flex h-8 items-center gap-2 rounded-full px-3 text-[12.5px] font-semibold ${pill.cls}`} role="status" aria-live="polite">
          <span className={`h-2 w-2 rounded-full ${pill.dot}`} aria-hidden />
          {pill.text}
        </span>
      </div>

      {unavailable && (
        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          The builder did not answer ({data?.reason || 'unreachable'}). The numbers below appear when it does.
        </div>
      )}
      {error && !unavailable && (
        <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-[13px] text-amber-700">{error}. Showing the last numbers we had.</div>
      )}

      {k && (
        <div className="mb-4 grid grid-cols-1 gap-px overflow-hidden rounded-[14px] border border-gray-200 bg-gray-200 sm:grid-cols-2">
          {/* Spending now is hidden: it projected the last 10 minutes onto an hour.
          <Kpi label="Spending now" span={`last ${k.model_rate_minutes} min`} value={`${usd(k.spending_now_per_hour)}/h`} foot={`models ${usd(k.models_per_hour)}/h from the last ${k.model_rate_minutes} minutes of calls, plus boxes running now ${usd(k.boxes_per_hour)}/h`} />
          */}
          <Kpi label="Model spend" span={`last ${minutes} min`} value={usd(chartTotal.cost)} foot={modelFoot} />
          {/* <Kpi label="Model spend today" span="since 00:00 Karachi" value={usd(k.models_today_usd)} foot="Every model call since midnight, Karachi time" /> */}
          <Kpi label="Boxes running" span="now" value={String(k.boxes_running)} foot={boxesFoot} />
        </div>
      )}

      {data && !unavailable && (
        <>
          <section className="mb-4 rounded-[14px] border border-gray-200 bg-white">
            <header className="px-5 pt-4">
              <h2 className="m-0 text-[14px] font-semibold text-gray-900">Spend per minute, last {minutes} minutes</h2>
            </header>
            <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-2 px-5 pt-3.5" aria-label="Agent kinds, spend per minute over the last 5 minutes">
              {KINDS.map((kk) => (
                <li key={kk} className="inline-flex items-center gap-2 text-[13px] text-gray-900">
                  <span className="h-[3px] w-4 rounded-full" style={{ background: colors[kk] }} aria-hidden />
                  {KIND_LABEL[kk]}
                  <span className="text-gray-500 tabular-nums">{usd(legendRate(kk))}/min</span>
                </li>
              ))}
            </ul>
            <div className="h-[280px] px-3 pb-3 pt-2 sm:px-5">
              <Line data={chartData} options={chartOptions} aria-label="Spend per minute by kind of agent" role="img" />
            </div>
            {k && k.untrusted_calls_window > 0 && (
              <p className="m-0 border-t border-gray-100 px-5 py-2.5 text-[12px] text-gray-500">
                {plural(k.untrusted_calls_window, 'call')} this hour ended without a trusted cost (errors or unreadable usage); they count at what was recorded.
              </p>
            )}
          </section>

          <section className="mb-4 rounded-[14px] border border-gray-200 bg-white">
            <header className="flex flex-wrap items-end justify-between gap-3 px-5 pt-4">
              <div>
                <h2 className="m-0 text-[14px] font-semibold text-gray-900">{workingTitle}</h2>
                <p className="m-0 mt-0.5 text-[12px] text-gray-500">An agent shows here when it makes a model call and leaves after 5 quiet minutes.</p>
              </div>
              {anyFilter && (
                <button type="button" onClick={clearFilters} className="h-8 rounded-lg px-3 text-[12.5px] font-semibold text-gray-700 hover:bg-gray-100">Clear filters</button>
              )}
            </header>
            <div className="grid grid-cols-2 gap-3 px-5 pt-3.5 md:grid-cols-4">
              <FilterSelect label="Client" value={client} onChange={(v) => { setClient(v); setProject(''); setRun(''); }} options={options.clients} />
              <FilterSelect label="Project" value={project} onChange={(v) => { setProject(v); setRun(''); }} options={options.projects} />
              <FilterSelect label="Agent" value={kind} onChange={(v) => { setKind(v); setRun(''); }} options={KINDS.map((kk) => ({ value: kk, label: KIND_LABEL[kk] }))} />
              {/* The work item or chat filter is hidden for now.
              <FilterSelect label="Work item or chat" value={run} onChange={setRun} options={options.runs} />
              */}
              <FilterSelect label="Model" value={model} onChange={setModel} options={options.models} />
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                    <th className="px-5 py-2.5 font-semibold">Client</th>
                    <th className="px-3 py-2.5 font-semibold">Project</th>
                    <th className="px-3 py-2.5 font-semibold">Agent</th>
                    {/* <th className="px-3 py-2.5 font-semibold">Working on</th> */}
                    <th className="px-3 py-2.5 font-semibold">Model now</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Running</th>
                    <th className="px-5 py-2.5 text-right font-semibold">Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {workingShown.map((w) => {
                    let running = duration(secondsSince(w.started_at, nowMs));
                    if (w.started_before_window) running = `${minutes} m+`;
                    return (
                      <tr key={w.run} className="border-t border-gray-100 align-top">
                        <td className="px-5 py-3 text-gray-900">{clientName(w.client_id)}</td>
                        <td className="px-3 py-3 text-gray-700">{w.project_title || 'No project'}</td>
                        <td className="px-3 py-3">
                          <span className="inline-flex items-center gap-1.5 text-gray-900">
                            <span className="h-2 w-2 rounded-full" style={{ background: colors[w.kind] }} aria-hidden />
                            {KIND_LABEL[w.kind]}
                          </span>
                        </td>
                        {/* <td className="max-w-[280px] px-3 py-3 text-gray-700"><span className="line-clamp-2">{w.working_on || 'Unlabelled run'}</span></td> */}
                        <td className="px-3 py-3 font-code text-[12px] text-gray-700">{shortModel(w.model)}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-gray-700">
                          {running}
                          <div className="text-[11.5px] text-gray-400">last call {duration(secondsSince(w.last_call_at, nowMs))} ago</div>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold tabular-nums text-gray-900">
                          {usd(w.spent_usd)}
                          {/* <div className="text-[11.5px] font-normal text-gray-400">{plural(w.calls, 'call')}</div> */}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {workingShown.length === 0 && (
                <p className="m-0 border-t border-gray-100 px-5 py-8 text-center text-[13px] text-gray-500">
                  {anyFilter && 'No agent matches these filters right now.'}
                  {!anyFilter && 'No agent is working right now. One shows here as soon as it makes a model call.'}
                </p>
              )}
            </div>
          </section>

          {/* Today by client is hidden for now.
          <section className="rounded-[14px] border border-gray-200 bg-white">
            <header className="px-5 pt-4">
              <h2 className="m-0 text-[14px] font-semibold text-gray-900">Today by client</h2>
              <p className="m-0 mt-0.5 text-[12px] text-gray-500">Model spend since 00:00 Karachi.</p>
            </header>
            <div className="px-5 pb-2 pt-2">
              {data.today_by_client.map((c) => (
                <div key={c.client_id} className="flex items-center justify-between gap-4 border-t border-gray-100 py-2.5 first:border-t-0">
                  <span className="min-w-0 truncate text-[13px] text-gray-900">{clientName(c.client_id)}</span>
                  <span className="shrink-0 text-right">
                    <span className="text-[13px] font-semibold tabular-nums text-gray-900">{usd(c.models_usd)}</span>
                  </span>
                </div>
              ))}
              {data.today_by_client.length === 0 && <p className="m-0 py-6 text-center text-[13px] text-gray-500">Nothing spent yet today.</p>}
            </div>
          </section>
          */}
        </>
      )}
    </div>
  );
};

export default LiveTab;
